import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../useApiClient.js';

export interface LogoutVariables {
  refresh?: string;
}

interface UseLogoutParams {
  onError?: (error: unknown) => void;
}

interface UseLogoutResult {
  logout: (variables?: LogoutVariables) => Promise<unknown>;
  isPending: boolean;
}

export const useLogout = ({ onError }: UseLogoutParams = {}): UseLogoutResult => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    // Blacklists the refresh token server-side and ends any active impersonation
    // session for the admin (SimpleJWT logout).
    mutationFn: async ({ refresh }: LogoutVariables) =>
      apiClient.users.createUsersAuthLogout({
        simpleJWTLogoutRequest: { refresh: refresh ?? '' },
      }),
    onError,
  });

  const logout = useCallback(
    (variables: LogoutVariables = {}) => {
      queryClient.clear();
      // Without a refresh token the SimpleJWT logout can only ever 4xx (it needs the
      // token to blacklist), so skip the doomed round-trip and just clear the cache.
      if (!variables.refresh) {
        return Promise.resolve();
      }
      return mutateAsync(variables);
    },
    [queryClient, mutateAsync]
  );

  return { logout, isPending };
};
